import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';
import { UserIcon } from '../components/Icons';

export const BetaFeedbackPage = () => {
  // Mock manuscript data
  const manuscriptId = 1;
  const manuscriptTitle = 'Echoes of the Archive';

  const [respondingTo, setRespondingTo] = useState<number | null>(null);
  const [responseText, setResponseText] = useState('');

  // Mock feedback data
  const [feedbackList, setFeedbackList] = useState([
    {
      id: 1,
      readerId: 103,
      readerName: 'Emma Rodriguez',
      readerPhoto: null,
      submittedDate: '3 days ago',
      overallRating: 5,
      categoryRatings: {
        plot: 5,
        characters: 5,
        pacing: 4,
        writing: 5,
        worldbuilding: 4,
      },
      overallFeedback: 'This manuscript is absolutely captivating! The protagonist, Margaret, is a beautifully complex character whose moral dilemma feels incredibly real and timely. The historical setting is meticulously researched and brings the era to life.\n\nThe pacing is mostly excellent, though I felt the middle section (chapters 8-12) could be tightened slightly. The coded letters subplot is brilliantly executed and kept me guessing until the end.\n\nThe writing is elegant without being overwrought, and the balance between historical detail and emotional resonance is perfect. I particularly loved the scenes in the archive - you can tell you understand that world intimately.',
      categoryFeedback: {
        plot: 'The plot structure is tight and well-executed. The mystery unfolds at just the right pace.',
        characters: 'Margaret is a wonderful protagonist. Her internal conflict feels authentic and her growth throughout the story is believable.',
        pacing: 'Generally excellent, though chapters 8-12 felt slightly slower than the rest.',
        writing: 'Beautiful prose that never gets in the way of the story. Your descriptions are vivid without being purple.',
        worldbuilding: 'The 1942 setting is incredibly well-researched and immersive.',
      },
      strengths: [
        'Complex, believable protagonist',
        'Excellent historical research',
        'Gripping mystery plot',
        'Beautiful writing style',
      ],
      areasForImprovement: [
        'Middle section pacing could be tightened',
        'Some secondary characters could be more developed',
      ],
      wouldRecommend: true,
      writerResponse: null,
    },
    {
      id: 2,
      readerId: 101,
      readerName: 'Sarah Mitchell',
      readerPhoto: null,
      submittedDate: '1 week ago',
      overallRating: 4,
      categoryRatings: {
        plot: 5,
        characters: 4,
        pacing: 4,
        writing: 4,
        worldbuilding: 5,
      },
      overallFeedback: 'A compelling historical mystery with excellent research and an engaging protagonist. The archive setting is unique and well-utilized, and the coded letters provide a strong driving force for the plot.\n\nMy main feedback would be around some of the supporting characters - particularly the love interest, who felt a bit underdeveloped compared to Margaret. I also wondered if some of the archival technical details might be simplified for general readers, though as someone with an archival studies background, I loved them!\n\nThe ending was satisfying and tied up the plot threads nicely. Overall, a very strong manuscript that I think historical fiction readers will love.',
      categoryFeedback: {
        plot: 'Excellent mystery structure with good use of tension and reveals.',
        characters: 'Margaret is wonderful, but some secondary characters need more depth.',
        pacing: 'Good overall, keeps the reader engaged throughout.',
        writing: 'Clean, professional writing that serves the story well.',
        worldbuilding: 'Outstanding historical detail and atmosphere.',
      },
      strengths: [
        'Unique setting (WWII archives)',
        'Strong central mystery',
        'Authentic historical details',
        'Engaging protagonist',
      ],
      areasForImprovement: [
        'Secondary character development',
        'Balance of technical archival details',
      ],
      wouldRecommend: true,
      writerResponse: {
        text: 'Thank you so much for this thoughtful feedback! Your point about the love interest is well-taken - I\'ll work on giving him more depth and agency in the revision. And I appreciate your perspective on the archival details. I\'ll review those sections with an eye toward accessibility.',
        submittedDate: '5 days ago',
      },
    },
    {
      id: 3,
      readerId: 102,
      readerName: 'Michael Chen',
      readerPhoto: null,
      submittedDate: '2 weeks ago',
      overallRating: 5,
      categoryRatings: {
        plot: 5,
        characters: 5,
        pacing: 5,
        writing: 4,
        worldbuilding: 4,
      },
      overallFeedback: 'From a structural and suspense perspective, this manuscript is excellent. The mystery elements are well-crafted with good red herrings and payoffs. Margaret\'s character arc is compelling and her stakes feel real.\n\nThe historical setting enhances rather than overwhelms the story, which is a difficult balance to achieve. I was consistently engaged throughout and found the ending both surprising and inevitable - the hallmark of good mystery writing.\n\nMy only minor critique would be that occasionally the prose can feel a bit formal, which sometimes creates distance from the emotional moments. But overall, this is a very strong manuscript that I think has real commercial potential.',
      categoryFeedback: {
        plot: 'Excellent mystery structure with well-placed reveals and satisfying resolution.',
        characters: 'Margaret is a standout - complex, flawed, and ultimately heroic.',
        pacing: 'Perfect. Never drags, never rushes.',
        writing: 'Generally very good, though occasionally a bit formal for the emotional beats.',
        worldbuilding: 'Good historical detail that supports but doesn\'t overwhelm the story.',
      },
      strengths: [
        'Excellent plot structure',
        'Strong protagonist with clear arc',
        'Perfect pacing',
        'Good balance of mystery and character',
      ],
      areasForImprovement: [
        'Prose can be formal in emotional moments',
        'Could lean into the thriller elements more',
      ],
      wouldRecommend: true,
      writerResponse: {
        text: 'This feedback is incredibly helpful, especially your observations about the prose in emotional scenes. I see exactly what you mean and will work on making those moments feel more immediate and visceral. Thank you!',
        submittedDate: '1 week ago',
      },
    },
  ]);

  const handleRespondClick = (feedbackId: number) => {
    const feedback = feedbackList.find(f => f.id === feedbackId);
    setRespondingTo(feedbackId);
    setResponseText(feedback?.writerResponse?.text || '');
  };

  const handleCancelResponse = () => {
    setRespondingTo(null);
    setResponseText('');
  };

  const handleSubmitResponse = (feedbackId: number) => {
    if (!responseText.trim()) {
      window.alert('Please enter a response');
      return;
    }

    setFeedbackList(feedbackList.map(feedback =>
      feedback.id === feedbackId
        ? {
            ...feedback,
            writerResponse: {
              text: responseText,
              submittedDate: 'just now',
            }
          }
        : feedback
    ));
    setRespondingTo(null);
    setResponseText('');
    window.alert('Response sent to beta reader!');
  };

  const handleExportFeedback = () => {
    console.log('Exporting all feedback for manuscript:', manuscriptId);
    window.alert('Feedback exported as PDF. Check your downloads folder.');
  };

  const renderStars = (rating: number) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              fontSize: '20px',
              color: star <= rating ? theme.colors.warning : theme.colors.border,
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const avgRating = feedbackList.length > 0
    ? (feedbackList.reduce((sum, f) => sum + f.overallRating, 0) / feedbackList.length).toFixed(1)
    : 0;

  const recommendCount = feedbackList.filter(f => f.wouldRecommend).length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '32px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = `/writer/manuscripts/${manuscriptId}`}
            style={{ marginBottom: '16px' }}
          >
            ← Back to Manuscript
          </Button>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Beta Reader Feedback
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            {manuscriptTitle}
          </p>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Total Feedback
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.primary,
              fontFamily: theme.fonts.monospace,
            }}>
              {feedbackList.length}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Avg Rating
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{
                fontSize: '32px',
                fontWeight: '400',
                color: theme.colors.warning,
                fontFamily: theme.fonts.monospace,
              }}>
                {avgRating}
              </span>
              <span style={{
                fontSize: '24px',
                color: theme.colors.warning,
              }}>
                ★
              </span>
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Would Recommend
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.success,
              fontFamily: theme.fonts.monospace,
            }}>
              {recommendCount}/{feedbackList.length}
            </div>
          </Card>
        </div>

        {/* Export Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '24px',
        }}>
          <Button
            variant="outline"
            onClick={handleExportFeedback}
          >
            Export All Feedback
          </Button>
        </div>

        {/* Feedback List */}
        {feedbackList.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                No feedback received yet
              </p>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {feedbackList.map((feedback) => (
              <Card key={feedback.id}>
                {/* Reader Header */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '24px',
                  paddingBottom: '20px',
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}>
                  {/* Profile Photo */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: theme.colors.primary,
                  }}>
                    <UserIcon width={30} height={30} />
                  </div>

                  {/* Reader Info */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '500',
                      marginBottom: '4px',
                      color: theme.colors.textPrimary,
                      cursor: 'pointer',
                    }}
                    onClick={() => window.location.href = `/reader/${feedback.readerId}`}
                    >
                      {feedback.readerName}
                    </h3>
                    <div style={{
                      fontSize: '14px',
                      color: theme.colors.textTertiary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      Submitted {feedback.submittedDate}
                    </div>
                  </div>

                  {/* Overall Rating */}
                  <div style={{
                    textAlign: 'right',
                  }}>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textSecondary,
                      marginBottom: '6px',
                      fontWeight: '500',
                    }}>
                      Overall Rating
                    </div>
                    {renderStars(feedback.overallRating)}
                  </div>
                </div>

                {/* Category Ratings */}
                <div style={{
                  backgroundColor: theme.colors.backgroundAlt,
                  padding: '20px',
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '24px',
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '16px',
                    color: theme.colors.textPrimary,
                  }}>
                    Category Ratings
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}>
                    {Object.entries(feedback.categoryRatings).map(([category, rating]) => (
                      <div key={category}>
                        <div style={{
                          fontSize: '13px',
                          color: theme.colors.textSecondary,
                          marginBottom: '6px',
                          textTransform: 'capitalize',
                        }}>
                          {category}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '2px',
                        }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              style={{
                                fontSize: '16px',
                                color: star <= rating ? theme.colors.warning : theme.colors.border,
                              }}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overall Feedback */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: theme.colors.textPrimary,
                  }}>
                    Overall Feedback
                  </h4>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.8',
                    color: theme.colors.textPrimary,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {feedback.overallFeedback}
                  </p>
                </div>

                {/* Category Feedback */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: theme.colors.textPrimary,
                  }}>
                    Detailed Feedback by Category
                  </h4>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}>
                    {Object.entries(feedback.categoryFeedback).map(([category, text]) => (
                      <div
                        key={category}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: theme.colors.backgroundAlt,
                          borderRadius: theme.borderRadius.small,
                        }}
                      >
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: theme.colors.textSecondary,
                          marginBottom: '6px',
                          textTransform: 'capitalize',
                        }}>
                          {category}
                        </div>
                        <p style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: theme.colors.textPrimary,
                        }}>
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Strengths & Areas for Improvement */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '24px',
                }}>
                  {/* Strengths */}
                  <div>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      marginBottom: '12px',
                      color: theme.colors.success,
                    }}>
                      Strengths
                    </h4>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      {feedback.strengths.map((strength, index) => (
                        <li
                          key={index}
                          style={{
                            fontSize: '15px',
                            color: theme.colors.textPrimary,
                            paddingLeft: '24px',
                            position: 'relative',
                          }}
                        >
                          <span style={{
                            position: 'absolute',
                            left: '0',
                            color: theme.colors.success,
                          }}>
                            ✓
                          </span>
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Areas for Improvement */}
                  <div>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      marginBottom: '12px',
                      color: theme.colors.warning,
                    }}>
                      Areas for Improvement
                    </h4>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      {feedback.areasForImprovement.map((area, index) => (
                        <li
                          key={index}
                          style={{
                            fontSize: '15px',
                            color: theme.colors.textPrimary,
                            paddingLeft: '24px',
                            position: 'relative',
                          }}
                        >
                          <span style={{
                            position: 'absolute',
                            left: '0',
                            color: theme.colors.warning,
                          }}>
                            →
                          </span>
                          {area}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Would Recommend Badge */}
                {feedback.wouldRecommend && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: theme.colors.successLight,
                    borderRadius: theme.borderRadius.medium,
                    marginBottom: '24px',
                  }}>
                    <span style={{
                      fontSize: '15px',
                      color: theme.colors.success,
                      fontWeight: '500',
                    }}>
                      ✓ Would recommend this manuscript to others
                    </span>
                  </div>
                )}

                {/* Writer Response Section */}
                <div style={{
                  paddingTop: '20px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  {feedback.writerResponse && respondingTo !== feedback.id ? (
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          color: theme.colors.textPrimary,
                        }}>
                          Your Response
                        </h4>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => handleRespondClick(feedback.id)}
                        >
                          Edit Response
                        </Button>
                      </div>
                      <div style={{
                        padding: '16px',
                        backgroundColor: theme.colors.primaryLight,
                        borderRadius: theme.borderRadius.medium,
                      }}>
                        <p style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: theme.colors.textPrimary,
                          marginBottom: '8px',
                        }}>
                          {feedback.writerResponse.text}
                        </p>
                        <div style={{
                          fontSize: '13px',
                          color: theme.colors.textTertiary,
                          fontFamily: theme.fonts.monospace,
                        }}>
                          Sent {feedback.writerResponse.submittedDate}
                        </div>
                      </div>
                    </div>
                  ) : respondingTo === feedback.id ? (
                    <div>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        marginBottom: '12px',
                        color: theme.colors.textPrimary,
                      }}>
                        {feedback.writerResponse ? 'Edit Your Response' : 'Respond to Feedback'}
                      </h4>
                      <Textarea
                        placeholder="Thank the beta reader and respond to their feedback..."
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        rows={6}
                      />
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '12px',
                      }}>
                        <Button onClick={() => handleSubmitResponse(feedback.id)}>
                          Send Response
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleCancelResponse}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => handleRespondClick(feedback.id)}
                    >
                      Respond to Feedback
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
